import { Accept, DependencyFailedError, FromBody, FromQuery, HttpStatusCodes, Inject, InternalServerError, NotFoundError, Result, UnauthorizedError } from '@resourceful-hono/core';
import AuthResource from '../index.ts';
import { decodeBase64Url, encodeBase64Url } from '@std/encoding/base64url';
import { z } from 'zod';
import { User } from '../../../../entities/User.ts';
import { WebAuthnCredential } from '../../../../entities/WebAuthnCredential.ts';
import { CHALLENGE_VALIDITY_PERIOD } from '../../../../services/OAuth/constants.ts';
import { hash } from '../../../../common/utils.ts';
import type { ExpectedAttestationResult } from 'fido2-lib';
import { POST_SCHEMA } from './schema.ts';
import { CustomContentTypes } from '../../../../common/enums.ts';
import { CredentialTypes } from '../../../../services/OAuth/enums.ts';
import { EnvironmentService } from '../../../../services/EnvironmentService.ts';
import { EmailService } from '../../../../services/Email/index.ts';
import { IsNull, Not } from 'typeorm';
import { AuthnVerification } from '@monocle-money/react-email';

export class AttestationResource extends AuthResource {
  @Inject()
  declare protected readonly email: EmailService;
  @Inject()
  declare private readonly env: EnvironmentService;

  public async GET(@FromQuery('challengeHash', z.string()) challengeHash: string) {
    const options = await this.webAuthn.attestationOptions();
    const credential = await WebAuthnCredential.findOne({
      where: {
        challengeHash,
      },
      relations: {
        user: true,
      },
    });

    if (!credential)
      throw new NotFoundError('No credential found.');

    if (!credential.challenge)
      throw new Error(
        `Challenge is null for WebAuthnCredential ${credential.id}`
      );

    const credentials = await WebAuthnCredential.find({
      where: {
        user: { id: credential.user.id },
        authenticatorCredentialId: Not(IsNull()),
      },
    });
    
    const excludeCredentials = credentials
      .map<{ id: BufferSource, type: 'public-key' }>(credential => {
        return {
          id: decodeBase64Url(credential.authenticatorCredentialId ?? ''),
          type: 'public-key',
        };
      });

    const challenge = decodeBase64Url(credential.challenge);
    const { user } = credential;
    options.user = {
      name: user.email,
      displayName: user.displayName,
      id: new Uint8Array([user.id]).buffer,
    };
    return Result(HttpStatusCodes.Ok, {
      credentialId: credential.id,
      excludeCredentials,
      publicKey: {
        ...options,
        userVerification: 'required' as const,
        challenge,
      },
    }, CustomContentTypes.Cbor);
  }

  public async PUT(@FromQuery('email', z.string().email()) email: string) {
    // check if user exists
    const user = await User.findOneBy({ email });
    if (!user) throw new NotFoundError(`User with email: ${email} not found`);
    const options = await this.webAuthn.attestationOptions();
    const challenge = new Uint8Array(options.challenge);
    const credential = await this.createCredential(
      user,
      encodeBase64Url(challenge)
    );
    options.user = {
      name: user.email,
      displayName: user.displayName,
      id: new Uint8Array([user.id]).buffer,
    };
    
    const clientOrigin = this.env.get('CLIENT_ORIGIN');
    const verificationLink = `${clientOrigin}/auth/${credential.challengeHash}`;

    this.email.builder()
      .setSubject('Login Attempt')
      .addRecipient(credential.user.email)
      .setBody(AuthnVerification({ verificationLink: verificationLink }))
      .send();
    return Result(HttpStatusCodes.Created);
  }

  @Accept([CustomContentTypes.Cbor])
  public async POST(
    @FromQuery('credentialId', z.coerce.number()) credentialId: number,
    @FromBody(POST_SCHEMA)
      clientAttestationResponse: z.infer<typeof POST_SCHEMA>
  ) {
    const credential = await WebAuthnCredential.findOne({
      where: {
        id: credentialId,
      },
      relations: {
        user: true,
      },
    });
    if (!credential)
      throw new InternalServerError('Credential not found.');
    if (!credential.challenge)
      throw new DependencyFailedError('Challenge not found');

    const attestationExpectations: ExpectedAttestationResult = {
      challenge: credential.challenge,
      origin: this.env.get('CLIENT_ORIGIN'),
      factor: 'first',
    };
    try {
      const { rawId, response } = clientAttestationResponse;
      const registration = await this.webAuthn.attestationResult(
        {
          rawId: rawId.buffer,
          response: {
            attestationObject: encodeBase64Url(response.attestationObject),
            clientDataJSON: encodeBase64Url(response.clientDataJSON),
          },
        },
        attestationExpectations
      );
      const counter = Number(registration.authnrData.get('counter'));
      const publicKey = String(registration.authnrData.get('credentialPublicKeyPem'));
      const authenticatorCredentialId = encodeBase64Url(registration.authnrData.get('credId'));
      await this.updateCredential(
        credentialId,
        authenticatorCredentialId,
        publicKey,
        counter
      );
      const authnTokenResponse = await this.oAuth.generateAuthnToken(
        credential.user.id,
        credential.id
      );
      return Result(HttpStatusCodes.Ok, authnTokenResponse);
    } catch (cause) {
      throw new UnauthorizedError('Invalid attestation', { cause });
    }
  }

  private async updateCredential(
    credentialId: number,
    authenticatorCredentialId: string,
    publicKey: string,
    counter: number
  ) {
    const webAuthnCredential = await WebAuthnCredential.findOneBy({
      id: credentialId,
    });
    if (!webAuthnCredential) return;
    webAuthnCredential.prevCounter = counter;
    webAuthnCredential.authenticatorCredentialId = authenticatorCredentialId;
    webAuthnCredential.publicKey = publicKey;
    webAuthnCredential.challenge = undefined;
    return webAuthnCredential.save();
  }

  private async createCredential(user: User, challenge: string) {
    const challengeHash = await hash(challenge);
    const challengeExpires = new Date(Date.now() + CHALLENGE_VALIDITY_PERIOD);
    const webAuthnCredential = new WebAuthnCredential();
    webAuthnCredential.challenge = challenge;
    webAuthnCredential.challengeExpires = challengeExpires;
    webAuthnCredential.challengeHash = challengeHash;
    webAuthnCredential.user = user;
    webAuthnCredential.type = CredentialTypes.WebAuthn;
    webAuthnCredential.trusted = true;
    return webAuthnCredential.save();
  }
}